import React, { useEffect, useState } from 'react';
import { api } from '../api/index.js';
import DynamicField from './DynamicField';

// Field grouping configuration
const FIELD_GROUPS = {
  primary: ['worktype', 'description', 'address', 'latitude', 'longitude', 'email', 'phone', 'name', 'image'],
  contact: [],
  details: ['notes', 'hazardLevel', 'status', 'category'],
  // Add any remaining fields to the 'other' group
  other: []
};

// Field order within groups (optional)
const FIELD_ORDER = {
  worktype: 1,
  description: 2,
  address: 3,
  latitude: 4,
  longitude: 5,
  email: 1,
  phone: 2,
  name: 3,
  status: 4,
  category: 5
};

// Removed required fields logic - no longer enforcing required fields

// All field options are now managed in the backend manifest

const EditIssue = props => {
  const { formControls = [], selectedIssue, onChange, onSave, onCancel } = props;
  const [workTypes, setWorkTypes] = useState([]);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(true);
  const [workTypesError, setWorkTypesError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Load active work types on component mount
  useEffect(() => {
    const loadWorkTypes = async () => {
      try {
        setLoadingWorkTypes(true);
        setWorkTypesError(null);

        // Use the new configurations API to get active work types
        const response = await api.configurations.getActiveWorkTypes();

        // The API returns an array of work type labels for the dropdown
        if (response && Array.isArray(response)) {
          setWorkTypes(response);
        } else if (response?.data && Array.isArray(response.data)) {
          setWorkTypes(response.data);
        } else {
          console.warn('Unexpected work types response format:', response);
          setWorkTypes([]);
        }
      } catch (error) {
        console.error('Error loading work types:', error);
        setWorkTypesError(error);
      } finally {
        setLoadingWorkTypes(false);
      }
    };

    loadWorkTypes();
  }, []);

  // Group form controls by category (formControls are already filtered to enabled only)
  const groupFormControls = () => {
    const groups = {
      primary: [],
      contact: [],
      details: [],
      other: []
    };

    // Get all field names from the already-filtered enabled controls
    const availableFields = new Set(formControls.map(ctrl => ctrl.name));

    // Only include available fields in their respective groups
    Object.entries(FIELD_GROUPS).forEach(([groupName, fieldList]) => {
      fieldList.forEach(fieldName => {
        if (availableFields.has(fieldName)) {
          groups[groupName].push(fieldName);
        }
      });
    });

    const usedFields = new Set(Object.values(groups).flat());

    // Add any remaining fields to the 'other' group, excluding worktype
    formControls
      .filter(ctrl =>
        !usedFields.has(ctrl.name) &&
        ctrl.name.toLowerCase() !== 'worktype'
      )
      .forEach(ctrl => {
        groups.other.push(ctrl.name);
      });

    return groups;
  };

  // Get form control by field name
  const getControl = (fieldName) => {
    // Special handling for worktype
    if (fieldName === 'worktype') {
      return {
        name: 'worktype',
        label: 'Work Type',
        type: 'select'
      };
    }

    // Find the control in formControls array
    const control = formControls.find(ctrl => ctrl.name === fieldName);

    if (!control) {
      console.warn(`No form control found for field: ${fieldName}`);
      // Return a default text control if none found
      return {
        name: fieldName,
        label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
        type: 'text'
      };
    }

    // Return control as-is from backend manifest (no required field logic)
    return control;
  };

  // Check if a field is available in form controls (all are enabled already)
  const isFieldAvailable = (fieldName) => {
    // Match control name case-insensitive
    const ctrl = formControls.find(ctrl => ctrl.name.toLowerCase() === fieldName.toLowerCase());
    return !!ctrl;
  };



  const isDisabled = () => {
    // Only check if terms are accepted - no required field validation
    return !termsAccepted;
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  // Enhanced save handler with validation
  const handleSaveWithValidation = () => {
    // Enable validation display for all fields
    setShowValidation(true);
    
    // Simple validation check for key fields (using same logic as DynamicField)
    const validateField = (name, value) => {
      const errors = [];
      
      switch (name.toLowerCase()) {
        case 'email':
          if (!value || value.trim() === '') {
            errors.push('Email is required');
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
            errors.push('Please enter a valid email address');
          }
          break;
          
        case 'description':
          if (!value || value.trim() === '') {
            errors.push('Description is required');
          } else if (value.trim().length < 10) {
            errors.push('Description must be at least 10 characters long');
          } else if (value.trim().length > 500) {
            errors.push('Description must be less than 500 characters');
          }
          break;
          
        case 'address':
          if (!value || value.trim() === '') {
            errors.push('Address is required');
          } else if (value.trim().length < 5) {
            errors.push('Address must be at least 5 characters long');
          } else if (value.trim().length > 200) {
            errors.push('Address must be less than 200 characters');
          }
          break;
      }
      
      return errors;
    };

    // Get validation errors for key fields - BUT ONLY IF THEY'RE AVAILABLE IN THE FORM
    const fieldsToValidate = ['email', 'description', 'address'].filter(fieldName => 
      isFieldAvailable(fieldName)
    );
    
    let hasValidationErrors = false;
    const allErrors = {};
    
    fieldsToValidate.forEach(fieldName => {
      const errors = validateField(fieldName, selectedIssue[fieldName]);
      if (errors.length > 0) {
        allErrors[fieldName] = errors;
        hasValidationErrors = true;
      }
    });
    
    // If validation fails, don't proceed with save
    if (hasValidationErrors) {
      console.log('Validation errors found for available fields:', allErrors);
      // Give a brief moment for validation errors to display, then scroll to first error
      setTimeout(() => {
        const firstErrorField = document.querySelector('.is-invalid');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    // If validation passes, proceed with original save
    console.log('Validation passed for fields:', fieldsToValidate);
    onSave();
  };

  const renderWorkTypeOptions = () => {
    if (loadingWorkTypes) {
      return <option value="">Loading work types...</option>;
    }

    if (workTypesError && workTypes.length === 0) {
      return <option value="">Error loading work types</option>;
    }

    return workTypes.map(workType => (
      <option key={workType} value={workType}>
        {workType}
      </option>
    ));
  };

  // Sort fields within a group
  const sortFields = (fields) => {
    return fields.sort((a, b) => (FIELD_ORDER[a] || 99) - (FIELD_ORDER[b] || 99));
  };

  // Filter available fields from a group
  const getAvailableFields = (fields) => {
    return fields.filter(field => isFieldAvailable(field));
  };

  if (!selectedIssue) {
    return <div />;
  }

  if (!Array.isArray(formControls)) {
    return (
      <div className="alert alert-info">
        Loading form controls...
      </div>
    );
  }

  const groups = groupFormControls();

  // Debug logging to help verify field filtering
  console.log('Available Form Controls:', formControls.map(ctrl => ({ name: ctrl.name, type: ctrl.type })));
  console.log('Grouped fields:', groups);

  return (
    <div>
      <h5 className='my-3'>Issue Details</h5>

      {/* Primary Information */}
      {getAvailableFields(groups.primary).length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-title mb-3">Primary Information</h6>
                <div className="row">
                  {sortFields(getAvailableFields(groups.primary)).map(fieldName => {
                  // Special handling for worktype
                  if (fieldName === 'worktype') {
                    return (
                      <div className="col-md-6 mb-3" key={fieldName}>
                        <label>Work Type</label>
                        <select
                          className='form-select'
                          name="worktype"
                          value={selectedIssue.worktype || ''}
                          onChange={onChange}
                        >
                          <option value="">Select Work Type</option>
                          {renderWorkTypeOptions()}
                          <option value="other">Other</option>
                        </select>
                      </div>
                    );
                  }

                  // Regular field handling
                  return (
                    <div className={`col-md-${fieldName === 'description' ? '12' : '6'} mb-3`} key={fieldName}>
                      <DynamicField
                        control={getControl(fieldName)}
                        value={selectedIssue[fieldName]}
                        onChange={onChange}
                        showValidation={showValidation}
                      />
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Details */}
      {getAvailableFields(groups.details).length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-title mb-3">Additional Details</h6>
                <div className="row">
                                   {sortFields(getAvailableFields(groups.details)).map(fieldName => (
                    <div className={`col-${fieldName === 'notes' ? '12' : 'md-6'} mb-3`} key={fieldName}>
                      <DynamicField
                        control={getControl(fieldName)}
                        value={selectedIssue[fieldName]}
                        onChange={onChange}
                        showValidation={showValidation}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Other Fields */}
      {getAvailableFields(groups.other).length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="card-title mb-3">Other Information</h6>
                <div className="row">
                                     {sortFields(getAvailableFields(groups.other)).map(fieldName => (
                    <div className="col-md-6 mb-3" key={fieldName}>
                      <DynamicField
                        control={getControl(fieldName)}
                        value={selectedIssue[fieldName]}
                        onChange={onChange}
                        showValidation={showValidation}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms and Buttons */}
      <div className='mt-3 mb-2'>
        <input
          type="checkbox"
          id="terms"
          name="terms"
          checked={termsAccepted}
          onChange={handleTermsChange}
        />
        <small className='ms-2' style={{ fontSize: '12px' }}>
          By submitting this request, I agree to the <a href="https://iamgis.com/terms" className='text-secondary'>Terms of Service</a> and <a href="https://www.iamgis.com/privacy" className='text-secondary'>Privacy Policy</a>.
        </small>
      </div>

      <button
        className={`my-3 w-100 btn ${isDisabled() ? 'btn-secondary' : 'btn-success'}`}
        onClick={handleSaveWithValidation}
        disabled={isDisabled()}
      >
        Submit
      </button>

      <button className="mb-4 w-100 btn btn-secondary" onClick={onCancel}>Cancel</button>
    </div>
  );
};

export default EditIssue;
